// Implementation of a temporary cache, that will keep key:value pairs for up to the specified time (after which it is cleaned up)
// Time is reset when touching a specific key (ie. calling get(), set() or has() with a given key)
// Note that the functions returning iterators and forEach does *not* reset timers
// Interface is the same as for a Map, except that the constructor takes a timeout value instead of an optional iterable
// (timeout is ms to keep a key:value in the cache before removing the key:value pair again)
export class TempCache<K,V>
{
    constructor(timeout: number)
    {
        this._timeout = timeout;
        setInterval(() => this._cleanup(), timeout);
    }

    get size(): number
    {
        return this._map.size;
    }

    clear(): void
    {
        this._timings.clear();
        this._map.clear();
    }

    delete(key: K): boolean
    {
        this._timings.delete(key);
        return this._map.delete(key);
    }

    entries(): IterableIterator<[K,V]>
    {
        return this._map.entries();
    }

    forEach(callbackfn: (value: V, key: K, map: TempCache<K, V>) => void): void
    {
        for (const [key, value] of this._map)
        {
            callbackfn(value, key, this);
        }
    }

    get(key: K): V|undefined
    {
        if (this._timings.has(key))
        {
            this._timings.set(key, Date.now());
        }
        return this._map.get(key);
    }

    has(key: K): boolean
    {
        if (this._timings.has(key))
        {
            this._timings.set(key, Date.now());
        }
        return this._map.has(key);
    }

    keys(): IterableIterator<K>
    {
        return this._map.keys();
    }

    set(key: K, value: V): TempCache<K,V>
    {
        this._timings.set(key, Date.now());
        this._map.set(key, value);
        return this;
    }

    values(): IterableIterator<V>
    {
        return this._map.values();
    }

    private _map: Map<K,V> = new Map();
    private _timings: Map<K,number> = new Map();
    private _timeout: number;

    private _cleanup()
    {
        const outdatedKeys: K[] = [];

        for (const [key, value] of this._timings)
        {
            if (Date.now() - value >= this._timeout)
            {
                outdatedKeys.push(key);
            }
        }

        for (const key of outdatedKeys)
        {
            this._map.delete(key);
            this._timings.delete(key);
        }
    }
}
